import { useState, useMemo, useContext } from "react";
import { Button, Table, Form, InputGroup, Pagination, Badge, Col } from "react-bootstrap";
import { Search, Edit3, Trash2, Eye, ShieldCheck, Plus } from "lucide-react";
import { ToastContainer } from 'react-toastify';
import { Dimcontext } from "../../context/pagecontext";
import { Edituser } from "./edituser";
import { Userdel } from "./userdel";
import { Adduser } from "./adduser";
import { Viewuser } from "./viewuser";
import { motion, AnimatePresence } from "framer-motion";
import '../css/user/userreg.css';
import { Datacontext } from "../../context/dataContext";
import { toast, Zoom } from 'react-toastify';

export const Userreg = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Added for selectable pagination
  const [modalState, setModalState] = useState({ type: null, id: null });
  const { setDim } = useContext(Dimcontext);
  const { users, fetchUsers } = useContext(Datacontext)

  // Updated logic for dynamic filtering and pagination
  const filteredUsers = useMemo(() => {
    return users.filter(u => u.name?.toLowerCase().includes(searchTerm.toLowerCase().trim()));
  }, [users, searchTerm]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination Handlers
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const showToast = (msg) => toast.warn(msg, { theme: "dark", transition: Zoom, position: "bottom-center" });
  
  const handleRowsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to page 1 on limit change
  };

  const openModal = (type, id = null) => {
    setModalState({ type, id });
    setDim(true);
  };

  return (
    <div className="document-container p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="page-title">User Accounts</h3>
        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center mb-4">
  <Col xs={12} md={5} lg={4}>
    <InputGroup className="search-bar-container">
      <InputGroup.Text className="bg-white border-end-0">
        <Search size={18} className="text-muted" />
      </InputGroup.Text>
      <Form.Control 
        className="border-start-0 ps-0"
        placeholder="Search..." 
        value={searchTerm} 
        onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
      />
    </InputGroup>
  </Col>
  
  <Button className="btn-add d-flex align-items-center justify-content-center gap-2" onClick={() => openModal('add')}>
    <Plus size={18} /> <span>Add User</span>
  </Button>
</div>
      </div>

      <div className="table-card shadow-sm">
        <Table hover striped responsive className="m-0">
          <thead>
            <tr>
              <th className="text-center" width="80">No.</th>
              <th>Full Name</th>
              <th>Role</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentItems.map((u, index) => (
              <tr key={u.id}>
                <td className="text-center">{indexOfFirstItem + index + 1}</td>
                <td className="fw-semibold text-uppercase">{u.name}</td>
                <td>
                  <Badge bg={u.role === 'admin' ? 'danger' : 'info'} className="text-uppercase">
                    {u.role === 'admin' ? <ShieldCheck size={12} className="me-1"/> : null} {u.role}
                  </Badge>
                </td>
                <td>
                  <div className="d-flex justify-content-center gap-2">
                    <Button variant="outline-success" size="sm" onClick={() => openModal('view', u.id)}><Eye size={16}/></Button>
                    <Button variant="outline-primary" size="sm" onClick={() => openModal('edit', u.id)}><Edit3 size={16}/></Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      disabled={u.role === 'admin'}
                      onClick={() => openModal('delete', u.id)}
                    >
                      <Trash2 size={16}/>
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* --- Added Bottom Pagination Bar --- */}
        {/* Standardized Pagination Footer */}
<div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
    
    {/* Left: Rows Per Page Selector */}
    <div className="d-flex align-items-center gap-2">
        <span className="text-muted small">Rows per page:</span>
        <Form.Select 
            size="sm" 
            style={{ width: '80px', borderRadius: '6px' }}
            value={itemsPerPage} 
            onChange={handleRowsPerPageChange}
        >
            {[5, 10, 20, 50].map(v => (
                <option key={v} value={v}>{v}</option>
            ))}
        </Form.Select>
    </div>

    {/* Middle: Results Summary */}
    <div className="text-muted small fw-medium">
        {filteredUsers.length > 0 ? (
            <>
                Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredUsers.length)}</b> of <b>{filteredUsers.length}</b> users
            </>
        ) : (
            "No users found"
        )}
    </div>

    {/* Right: Modern Navigation Controls */}
    <Pagination className="mb-0 custom-pagination shadow-sm">
        <Pagination.First 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(1)} 
        />
        <Pagination.Prev 
            disabled={currentPage === 1} 
            onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
        />

        <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>

        <Pagination.Next 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
        />
        <Pagination.Last 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => handlePageChange(totalPages)} 
        />
    </Pagination>
</div>
      </div>

      {/* --- Modal AnimatePresence Logic --- */}
      <ToastContainer />
      <AnimatePresence mode="wait">
        {modalState.type && (
          <div className="custom-modal-backdrop">
            <motion.div 
              initial={{ y: 50, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              exit={{ y: 50, opacity: 0 }} 
              className="modal-content-wrapper"
            >
              {modalState.type === 'add' && <Adduser set_add={() => setModalState({})} userg={fetchUsers} showToast={showToast} users={users}/>}
              {modalState.type === 'edit' && <Edituser set_edit={() => setModalState({})} id={modalState.id} userg={fetchUsers} showToast={showToast}/>}
              {modalState.type === 'delete' && <Userdel set_del={() => setModalState({})} id={modalState.id} userg={fetchUsers} showToast={showToast}/>}
              {modalState.type === 'view' && <Viewuser set_view={() => setModalState({})} id={modalState.id}/>}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};